import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Copy, ExternalLink, Share2, Sparkles, TrendingUp, Wallet, ShoppingCart, MousePointerClick } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));
const pct = (n) => `${Number(n || 0).toFixed(0)}%`;

function CopyBtn({ value, label = "Copy", testid }) {
  return (
    <button
      onClick={() => navigator.clipboard.writeText(value).then(() => toast.success("Copied!"))}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-300 bg-white text-xs font-mono uppercase tracking-widest text-slate-700 hover:bg-slate-50"
    >
      <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> {label}
    </button>
  );
}

function StatCard({ icon: I, label, value, sub, testid }) {
  return (
    <div className="border border-slate-200 rounded-md p-5 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2">
        <I className="w-4 h-4 text-blue-600" strokeWidth={1.75} />
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      </div>
      <div className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function PendingPortal({ affiliate }) {
  const applied = affiliate?.application;
  return (
    <div className="border border-amber-200 rounded-md bg-amber-50 p-6" data-testid="affiliate-pending">
      <div className="eyebrow text-amber-800 mb-1">Application Pending</div>
      <h2 className="font-display text-2xl font-semibold text-slate-900">We're reviewing your application.</h2>
      <p className="mt-2 text-sm text-slate-700">
        You'll receive access to your referral link and affiliate dashboard once you're approved.
      </p>
      {applied?.application_id && (
        <p className="mt-2 text-xs font-mono text-slate-500">Reference: {applied.application_id}</p>
      )}
    </div>
  );
}

function NonePortal({ signupEnabled }) {
  if (!signupEnabled) {
    return (
      <div className="border border-slate-200 rounded-md bg-white p-8 text-center" data-testid="affiliate-signup-closed">
        <p className="text-slate-600 text-sm">Affiliate signups are currently closed. Check back soon.</p>
      </div>
    );
  }
  return (
    <div className="border border-slate-200 rounded-md bg-white p-8 text-center" data-testid="affiliate-cta">
      <Sparkles className="w-8 h-8 text-blue-600 mx-auto mb-3" strokeWidth={1.75} />
      <h2 className="font-display text-2xl font-semibold text-slate-900">Become an affiliate.</h2>
      <p className="mt-2 text-slate-600 text-sm max-w-md mx-auto">
        Earn commission by referring new customers. Higher tiers, higher rates — up to 25%.
      </p>
      <Link
        to="/affiliates/apply"
        data-testid="affiliate-apply-link"
        className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
      >
        Apply now →
      </Link>
    </div>
  );
}

export default function AffiliateOverview() {
  const { affiliate: a, affLoaded } = useOutletContext() || {};
  const orderProgress = useMemo(() => {
    if (!a?.next_tier) return 100;
    const target = a.next_tier.min_orders;
    return Math.min(100, Math.round(((a.mtd_orders || 0) / Math.max(1, target)) * 100));
  }, [a]);

  if (!affLoaded) return null;
  if (!a || a.status === "none") return <NonePortal signupEnabled={a?.signup_enabled ?? true} />;
  if (a.status === "pending") return <PendingPortal affiliate={a} />;
  if (!["active", "paused"].includes(a.status)) {
    return (
      <div className="border border-slate-200 rounded-md bg-white p-6" data-testid="affiliate-inactive">
        <p className="text-slate-700 text-sm">
          Your affiliate account is currently <span className="font-semibold">{a.status}</span>. Contact support if you believe this is a mistake.
        </p>
      </div>
    );
  }

  const shareText = "Peps 4 Pets — research-grade peptides.";
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareText, url: a.referral_url }); } catch {}
    } else {
      navigator.clipboard.writeText(a.referral_url).then(() => toast.success("Link copied — share it anywhere"));
    }
  };

  const currentPct = a.current_tier?.commission_percent ?? 0;
  const nextPct = a.next_tier?.commission_percent;

  return (
    <div className="space-y-6" data-testid="affiliate-overview">
      {/* Hero: link + code + tier progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Link + Code */}
        <div className="lg:col-span-2 border border-slate-200 rounded-md bg-white p-6" data-testid="affiliate-link-card">
          <div className="eyebrow mb-2">Your Affiliate Link</div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
            <div
              data-testid="affiliate-link-value"
              className="flex-1 min-w-0 truncate font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-800"
              title={a.referral_url}
            >
              {a.referral_url}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <CopyBtn value={a.referral_url} label="Copy Link" testid="copy-affiliate-link" />
              <a
                href={a.referral_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="open-affiliate-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-300 bg-white text-xs font-mono uppercase tracking-widest text-slate-700 hover:bg-slate-50"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.75} /> Open
              </a>
              <button
                onClick={handleShare}
                data-testid="share-affiliate-link"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-slate-300 bg-white text-xs font-mono uppercase tracking-widest text-slate-700 hover:bg-slate-50"
              >
                <Share2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Share
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Referral Code</div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-xl font-semibold text-slate-900" data-testid="affiliate-code-value">
                  {a.affiliate.referral_code}
                </div>
                <CopyBtn value={a.affiliate.referral_code} label="Copy" testid="copy-affiliate-code" />
              </div>
              <div className="mt-2 text-xs text-slate-600">
                Customer discount: <span className="font-semibold">
                  {a.coupon?.discount_type === "percent"
                    ? `${a.coupon.discount_value}% off`
                    : a.coupon
                      ? fmtMoney(a.coupon.discount_value) + " off"
                      : "—"}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500 leading-snug">
                Your customers receive the discount above. Your commission is determined by your current tier.
              </div>
            </div>
            <div className="border border-slate-200 rounded-md p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Current Commission</div>
              <div className="font-display text-3xl font-semibold text-blue-700" data-testid="affiliate-current-rate">
                {pct(currentPct)}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                Tier: <span className="font-semibold">{a.current_tier?.name || "—"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tier progress card */}
        <div className="border border-slate-200 rounded-md bg-white p-6" data-testid="affiliate-progress-card">
          <div className="eyebrow mb-2">Tier Progress</div>
          <div className="font-display text-2xl font-semibold text-slate-900" data-testid="mtd-orders">
            {a.mtd_orders} <span className="text-sm font-normal text-slate-500">/ {a.next_tier?.min_orders ?? a.current_tier?.min_orders ?? 0} orders</span>
          </div>
          <div className="mt-3 h-2.5 bg-slate-100 rounded overflow-hidden">
            <div
              data-testid="progress-bar"
              className="h-full bg-blue-600 transition-all"
              style={{ width: `${orderProgress}%` }}
            />
          </div>
          {a.next_tier ? (
            <p className="mt-3 text-xs text-slate-600" data-testid="progress-hint">
              {a.mtd_needed_for_next} more {a.mtd_needed_for_next === 1 ? "order" : "orders"} to unlock{" "}
              <span className="font-semibold">{a.next_tier.name} — {pct(nextPct)}</span>.
            </p>
          ) : (
            <p className="mt-3 text-xs text-slate-600" data-testid="progress-max">
              You've reached the highest affiliate tier.
            </p>
          )}
        </div>
      </div>

      {/* Earnings — This Month */}
      <section aria-labelledby="mtd-earnings">
        <div id="mtd-earnings" className="eyebrow mb-2">This Month</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={MousePointerClick} label="Clicks" value={a.mtd_clicks || 0} testid="mtd-clicks" />
          <StatCard icon={ShoppingCart} label="Referred Orders" value={a.mtd_orders} testid="mtd-orders-stat" />
          <StatCard icon={TrendingUp} label="Revenue Generated" value={fmtMoney(a.mtd_revenue)} testid="mtd-revenue" />
          <StatCard icon={Wallet} label="Commission Earned" value={fmtMoney(a.mtd_commission)} testid="mtd-commission" />
        </div>
      </section>

      {/* Earnings — Current */}
      <section aria-labelledby="current-earnings">
        <div id="current-earnings" className="eyebrow mb-2">Current Earnings</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard icon={Wallet} label="Pending Commission" value={fmtMoney(a.pending_commission)} sub={`Held for ${a.settings?.hold_days || 30} days`} testid="pending-commission" />
          <StatCard icon={Wallet} label="Payable Commission" value={fmtMoney(a.payable_commission)} sub="Ready to be paid out" testid="payable-commission" />
          <StatCard icon={Wallet} label="Paid This Month" value={fmtMoney(a.paid_this_month)} testid="paid-this-month" />
        </div>
      </section>

      {/* Lifetime */}
      <section aria-labelledby="lifetime-earnings">
        <div id="lifetime-earnings" className="eyebrow mb-2">Lifetime</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={ShoppingCart} label="Total Orders" value={a.lifetime_orders} testid="lifetime-orders" />
          <StatCard icon={TrendingUp} label="Total Revenue" value={fmtMoney(a.lifetime_revenue)} testid="lifetime-revenue" />
          <StatCard icon={Wallet} label="Total Commission" value={fmtMoney(a.lifetime_commission)} testid="lifetime-commission" />
          <StatCard icon={Wallet} label="Total Paid" value={fmtMoney(a.paid_lifetime)} testid="lifetime-paid" />
        </div>
      </section>

      {/* Tiers */}
      <section aria-labelledby="tier-list">
        <div id="tier-list" className="eyebrow mb-2">Your Affiliate Tier</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tier-list">
          {a.tiers.map((t) => {
            const isCurrent = a.current_tier && t.tier_id === a.current_tier.tier_id;
            return (
              <div
                key={t.tier_id}
                data-testid={`tier-${t.name.toLowerCase()}`}
                className={`border rounded-md p-4 bg-white ${isCurrent ? "border-blue-500 ring-1 ring-blue-500" : "border-slate-200"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-lg font-semibold text-slate-900">{t.name}</div>
                  {isCurrent && <span className="text-[9px] font-mono uppercase tracking-widest text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">Current</span>}
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {t.min_orders}{t.max_orders != null ? `–${t.max_orders}` : "+"} orders
                </div>
                <div className="mt-2 font-mono text-xl text-blue-700 font-semibold">{pct(t.commission_percent)}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* How commissions work */}
      <section className="border border-slate-200 rounded-md bg-slate-50 p-5">
        <div className="eyebrow mb-2">How commissions work</div>
        <p className="text-sm text-slate-700 leading-relaxed">
          Commission tiers are progressive each month. Each qualifying order climbs you closer to the next tier — and higher tier rates
          apply to future qualifying orders after the threshold is reached. Previous orders in the same month are not recalculated.
          Monthly tier progress resets at the beginning of each calendar month.
        </p>
      </section>

      {/* Recent Referred Orders */}
      <RecentOrders />
    </div>
  );
}

// -------------------------------------------------------------------
//   Recent referred orders — pulls first page of /me/affiliate/commissions
// -------------------------------------------------------------------
function RecentOrders() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/me/affiliate/commissions?limit=5").then((r) => setRows(r.data.items)).catch(() => setRows([]));
  }, []);
  return (
    <section aria-labelledby="recent-orders">
      <div className="flex items-center justify-between mb-2">
        <div id="recent-orders" className="eyebrow">Recent Referred Orders</div>
        <Link to="/dashboard/affiliate/orders" className="text-xs font-mono uppercase tracking-widest text-blue-700 hover:underline" data-testid="view-all-orders">
          View all →
        </Link>
      </div>
      <div className="border border-slate-200 rounded-md bg-white overflow-hidden" data-testid="recent-orders-table">
        {rows === null && <div className="p-4 text-xs font-mono text-slate-400">Loading…</div>}
        {rows && rows.length === 0 && (
          <div className="p-6 text-sm text-slate-500 text-center">No referred orders yet. Share your link to get your first commission!</div>
        )}
        {rows && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Order #</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Date</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">Order</th>
                <th className="text-right px-3 py-2">Rate</th>
                <th className="text-right px-3 py-2">Commission</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.commission_id} className="border-t border-slate-200">
                  <td className="px-3 py-2 font-mono text-xs">{c.order_number || c.order_id.slice(-8)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 hidden sm:table-cell">{(c.order_date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right hidden md:table-cell">{fmtMoney(c.eligible_amount)}</td>
                  <td className="px-3 py-2 text-right text-xs">{pct(c.commission_rate)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtMoney(c.commission_amount)}</td>
                  <td className="px-3 py-2"><StatusPill status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export function StatusPill({ status }) {
  const map = {
    pending:   "bg-amber-100 text-amber-800",
    payable:   "bg-blue-100 text-blue-800",
    paid:      "bg-emerald-100 text-emerald-800",
    reversed:  "bg-rose-100 text-rose-800",
    cancelled: "bg-slate-200 text-slate-700",
  };
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
}
